import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import {
    CmsCtaTarget,
    CmsDraftPageContent,
    CmsLeadFormLayout,
    CmsMandatoryDisclosureSnapshot,
    CmsPageModuleContent,
    CmsPageModuleType,
    CmsPreviewView,
    CmsQuoteSectionLayout,
    CmsSnapshot,
} from '@ndvr/cms-api';
import { cmsModuleCss, CmsModuleRenderer, LEAD_FORM_ANCHOR_ID, themeStyle } from '@ndvr/cms-renderer-modules';
import type { CmsAssetRenderProps, CmsModuleRenderProps, CmsThemeTokens } from '@ndvr/cms-renderer-modules';

import { DEFAULT_LEAD_FORM_SUBMIT_LABEL } from './cms-lead-form-defaults';
import { CmsQueryKeys, fetchCmsAssetPreview } from './cms-queries';

export const cmsPreviewTheme: CmsThemeTokens = {
    colorBackground: '#f8f7f5', // claro linen
    colorSurface: '#ffffff',
    colorText: '#2c2424', // claro cocoa
    colorMutedText: '#5c5252', // cocoa-70
    colorAccent: '#69c8cf', // claro aqua
    colorAccentText: '#2c2424',
    colorBorder: '#efece7', // linen-shade
    fontFamily: '"halyard-text", "Source Sans 3", "Source Sans Pro", system-ui, -apple-system, sans-serif',
    maxWidth: '1200px',
};

// Claro brand fonts (ivypresto-display + halyard-text) are loaded once by the
// command-center app shell (index.html, Adobe Typekit kit emr7xgx).
export const CmsSharedStyles = () => <style>{cmsModuleCss}</style>;

const hasText = (value?: string): boolean => !!value?.trim();

const ctaTarget = (module: CmsPageModuleContent): CmsCtaTarget => {
    if (module.cta?.target) {
        return module.cta.target;
    }
    return hasText(module.cta?.url) ? CmsCtaTarget.URL : CmsCtaTarget.LEAD_FORM;
};

const moduleToRenderProps = (
    module: CmsPageModuleContent,
    assetPreviews: Record<string, CmsAssetRenderProps> = {},
): CmsModuleRenderProps | undefined => {
    switch (module.moduleType) {
        case CmsPageModuleType.HERO: {
            const imageAssetId = module.hero?.imageAssetId ? String(module.hero.imageAssetId) : undefined;
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'HERO',
                hero: {
                    eyebrow: module.hero?.eyebrow,
                    headline: module.hero?.headline ?? '',
                    body: module.hero?.body,
                    image: imageAssetId ? assetPreviews[imageAssetId] : undefined,
                },
            };
        }
        case CmsPageModuleType.CTA:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'CTA',
                cta: {
                    label: module.cta?.label ?? '',
                    url:
                        ctaTarget(module) === CmsCtaTarget.LEAD_FORM
                            ? `#${LEAD_FORM_ANCHOR_ID}`
                            : (module.cta?.url ?? ''),
                },
            };
        case CmsPageModuleType.ADVISOR_PROFILE: {
            const imageAssetId = module.advisorProfile?.imageAssetId
                ? String(module.advisorProfile.imageAssetId)
                : undefined;
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'ADVISOR_PROFILE',
                advisorProfile: {
                    name: module.advisorProfile?.name,
                    title: module.advisorProfile?.title,
                    credentials: module.advisorProfile?.credentials?.filter(hasText),
                    bio: module.advisorProfile?.bio,
                    phone: module.advisorProfile?.phone,
                    email: module.advisorProfile?.email,
                    calendarUrl: module.advisorProfile?.calendarUrl,
                    linkedInUrl: module.advisorProfile?.linkedInUrl,
                    location: module.advisorProfile?.location,
                    image: imageAssetId ? assetPreviews[imageAssetId] : undefined,
                },
            };
        }
        case CmsPageModuleType.CONTACT_SECTION:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'CONTACT_SECTION',
                contactSection: {
                    heading: module.contactSection?.heading,
                    body: module.contactSection?.body,
                    phone: module.contactSection?.phone,
                    email: module.contactSection?.email,
                    calendarUrl: module.contactSection?.calendarUrl,
                    addressLines: module.contactSection?.addressLines,
                    ctaLabel: module.contactSection?.ctaLabel,
                },
            };
        case CmsPageModuleType.LEAD_FORM:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'LEAD_FORM',
                leadForm: {
                    eyebrow: module.leadForm?.eyebrow,
                    heading: module.leadForm?.heading,
                    body: module.leadForm?.body,
                    layout: module.leadForm?.layout ?? CmsLeadFormLayout.SPLIT,
                    submitLabel: module.leadForm?.submitLabel?.trim() || DEFAULT_LEAD_FORM_SUBMIT_LABEL,
                    successMessage: module.leadForm?.successMessage,
                },
            };
        case CmsPageModuleType.INFO_CARD_GRID:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'INFO_CARD_GRID',
                infoCardGrid: {
                    ...module.infoCardGrid,
                    items: module.infoCardGrid?.items ?? [],
                },
            };
        case CmsPageModuleType.MEDIA_TEXT_SECTION: {
            const imageAssetId = module.mediaTextSection?.imageAssetId
                ? String(module.mediaTextSection.imageAssetId)
                : undefined;
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'MEDIA_TEXT_SECTION',
                mediaTextSection: {
                    eyebrow: module.mediaTextSection?.eyebrow,
                    heading: module.mediaTextSection?.heading,
                    body: module.mediaTextSection?.body,
                    layout: module.mediaTextSection?.layout,
                    image: imageAssetId ? assetPreviews[imageAssetId] : undefined,
                    ctaLabel: module.mediaTextSection?.ctaLabel,
                    ctaUrl: module.mediaTextSection?.ctaUrl,
                },
            };
        }
        case CmsPageModuleType.PROCESS_STEPS:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'PROCESS_STEPS',
                processSteps: {
                    ...module.processSteps,
                    steps: module.processSteps?.steps ?? [],
                },
            };
        case CmsPageModuleType.QUOTE_SECTION: {
            const layout = module.quoteSection?.layout ?? CmsQuoteSectionLayout.LEFT;
            const imageAssetId =
                layout !== CmsQuoteSectionLayout.CENTERED && module.quoteSection?.imageAssetId
                    ? String(module.quoteSection.imageAssetId)
                    : undefined;
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'QUOTE_SECTION',
                quoteSection: {
                    eyebrow: module.quoteSection?.eyebrow,
                    quote: module.quoteSection?.quote ?? '',
                    attributionName: module.quoteSection?.attributionName,
                    attributionTitle: module.quoteSection?.attributionTitle,
                    layout,
                    image: imageAssetId ? assetPreviews[imageAssetId] : undefined,
                    ctaLabel: module.quoteSection?.ctaLabel,
                    ctaUrl: module.quoteSection?.ctaUrl,
                },
            };
        }
        case CmsPageModuleType.RESOURCE_LIST:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'RESOURCE_LIST',
                resourceList: {
                    ...module.resourceList,
                    items: (module.resourceList?.items ?? []).map((item) => ({
                        title: item.title,
                        description: item.description,
                        linkLabel: item.linkLabel ?? '',
                        url: item.url ?? '',
                    })),
                },
            };
        case CmsPageModuleType.IMPORTANT_DISCLOSURES:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'IMPORTANT_DISCLOSURES',
                importantDisclosures: {
                    heading: module.importantDisclosures?.heading,
                    paragraphs: (module.importantDisclosures?.paragraphs ?? []).map((item) => ({
                        body: item.body,
                    })),
                    footnotes: (module.importantDisclosures?.footnotes ?? []).map((item) => ({
                        key: item.key,
                        label: item.label,
                        body: item.body,
                    })),
                },
            };
        case CmsPageModuleType.FAQ:
            return {
                moduleId: module.cmsPageModuleGUID,
                moduleType: 'FAQ',
                faq: {
                    ...module.faq,
                    items: (module.faq?.items ?? []).map((item) => ({
                        question: item.question ?? '',
                        answer: item.answer ?? '',
                    })),
                },
            };
        default:
            return undefined;
    }
};

const isCmsModuleRenderProps = (module: CmsModuleRenderProps | undefined): module is CmsModuleRenderProps =>
    module !== undefined;

const EMPTY_MANDATORY_DISCLOSURE: CmsMandatoryDisclosureSnapshot = {
    version: 'Draft',
    body: '',
};

const mandatoryDisclosureToRenderProps = (disclosure?: CmsMandatoryDisclosureSnapshot) =>
    disclosure ?? EMPTY_MANDATORY_DISCLOSURE;

const collectDraftAssetIds = (draft: CmsDraftPageContent): string[] =>
    Array.from(
        new Set(
            draft.modules
                .flatMap((module) => [
                    module.moduleType === CmsPageModuleType.HERO ? module.hero?.imageAssetId : undefined,
                    module.moduleType === CmsPageModuleType.ADVISOR_PROFILE
                        ? module.advisorProfile?.imageAssetId
                        : undefined,
                    module.moduleType === CmsPageModuleType.MEDIA_TEXT_SECTION
                        ? module.mediaTextSection?.imageAssetId
                        : undefined,
                    module.moduleType === CmsPageModuleType.QUOTE_SECTION &&
                    module.quoteSection?.layout !== CmsQuoteSectionLayout.CENTERED
                        ? module.quoteSection?.imageAssetId
                        : undefined,
                ])
                .filter((assetId): assetId is string => !!assetId)
                .map(String),
        ),
    );

const useCmsDraftAssetPreviews = (draft: CmsDraftPageContent): Record<string, CmsAssetRenderProps> => {
    const assetIds = useMemo(() => collectDraftAssetIds(draft), [draft]);
    const assetQueries = useQueries({
        queries: assetIds.map((assetId) => ({
            queryKey: CmsQueryKeys.assetPreview(assetId),
            enabled: !!assetId,
            retry: false,
            queryFn: () => fetchCmsAssetPreview(assetId),
        })),
    });
    const [previews, setPreviews] = useState<Record<string, CmsAssetRenderProps>>({});
    const assetIdSignature = assetIds.join('|');
    const assetContentSignature = assetQueries.map((query) => query.dataUpdatedAt).join('|');

    useEffect(() => {
        const objectUrls: string[] = [];
        const nextPreviews = assetQueries.reduce<Record<string, CmsAssetRenderProps>>((acc, query, index) => {
            if (query.data) {
                const assetId = assetIds[index];
                const url = URL.createObjectURL(query.data.content);
                objectUrls.push(url);
                acc[assetId] = {
                    assetId,
                    src: url,
                    alt: query.data.asset.altText ?? '',
                    caption: query.data.asset.caption,
                    contentType: query.data.asset.contentType,
                };
            }
            return acc;
        }, {});

        setPreviews(nextPreviews);

        return () => {
            objectUrls.forEach((url) => URL.revokeObjectURL(url));
        };
        // Query result objects are not stable; these signatures track the content changes that require new object URLs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetIdSignature, assetContentSignature]);

    return previews;
};

export const snapshotToRenderProps = (snapshot: CmsSnapshot) => ({
    slug: snapshot.slug,
    siteName: snapshot.siteName,
    pageTitle: snapshot.pageTitle,
    metaDescription: snapshot.metaDescription,
    advisorDisplayName: snapshot.advisorDisplayName,
    mandatoryDisclosure: mandatoryDisclosureToRenderProps(snapshot.mandatoryDisclosure),
    modules: (snapshot.modules ?? []).map((module) => moduleToRenderProps(module)).filter(isCmsModuleRenderProps),
    longFormContent: [],
    theme: cmsPreviewTheme,
});

export const CmsSnapshotPreview = ({ preview }: { preview: CmsPreviewView }) => (
    <div className="overflow-hidden rounded-md border bg-white">
        <CmsSharedStyles />
        <CmsModuleRenderer {...snapshotToRenderProps(preview.snapshot)} />
    </div>
);

type CmsModuleListPreviewProps = {
    draft: CmsDraftPageContent;
    selectedIndex?: number;
    onSelectIndex?: (index: number) => void;
};

/**
 * Renders the draft's modules in list order, mounting only the modules that are present.
 */
export const CmsModuleListPreview = ({ draft, selectedIndex, onSelectIndex }: CmsModuleListPreviewProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const assetPreviews = useCmsDraftAssetPreviews(draft);
    const renderModules = draft.modules
        .map((module) => moduleToRenderProps(module, assetPreviews))
        .filter(isCmsModuleRenderProps);

    const handlePreviewClick = (event: MouseEvent<HTMLDivElement>) => {
        if (!onSelectIndex) {
            return;
        }

        const container = containerRef.current;
        const target = event.target instanceof Element ? event.target : null;
        const section = target?.closest('[data-cms-module]');
        const isNativeDisclosureControl = !!target?.closest('summary');
        const internalPreviewLink = target?.closest<HTMLAnchorElement>('a[href^="#"]');

        if (!container || !section || !container.contains(section)) {
            return;
        }

        if (internalPreviewLink) {
            event.preventDefault();
            const targetId = decodeURIComponent(internalPreviewLink.hash.slice(1));
            const targetElement = targetId ? container.ownerDocument.getElementById(targetId) : null;
            if (targetElement && container.contains(targetElement)) {
                window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}${window.location.search}#${targetId}`,
                );
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        const index = Array.from(container.querySelectorAll('[data-cms-module]')).indexOf(section);

        if (index >= 0) {
            if (!isNativeDisclosureControl) {
                event.preventDefault();
            }
            onSelectIndex(index);
        }
    };

    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        const sections = Array.from(container.querySelectorAll('[data-cms-module]'));

        sections.forEach((section) => section.classList.remove('cms-preview-module--selected'));

        if (typeof selectedIndex === 'number' && selectedIndex >= 0 && selectedIndex < sections.length) {
            sections[selectedIndex].classList.add('cms-preview-module--selected');
        }
    }, [selectedIndex, draft]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container || typeof selectedIndex !== 'number') {
            return;
        }

        const sections = Array.from(container.querySelectorAll('[data-cms-module]'));

        sections[selectedIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [selectedIndex]);

    return (
        <div
            ref={containerRef}
            className="overflow-hidden rounded-md border bg-white"
            onClick={onSelectIndex ? handlePreviewClick : undefined}
        >
            <CmsSharedStyles />
            {onSelectIndex ? (
                <style>{`
                    [data-cms-module] { cursor: pointer; }
                    [data-cms-module]:hover { outline: 1px dashed rgba(105,200,207,0.6); outline-offset: -1px; }
                    .cms-preview-module--selected { outline: 2px solid #69c8cf; outline-offset: -2px; }
                `}</style>
            ) : null}
            {renderModules.length ? (
                <CmsModuleRenderer
                    slug=""
                    siteName=""
                    pageTitle={draft.pageTitle}
                    advisorDisplayName=""
                    mandatoryDisclosure={mandatoryDisclosureToRenderProps(draft.mandatoryDisclosure)}
                    modules={renderModules}
                    longFormContent={[]}
                    theme={cmsPreviewTheme}
                />
            ) : null}
            {!renderModules.length ? (
                <main className="cms-page" style={themeStyle(cmsPreviewTheme)}>
                    <p className="text-muted-foreground p-10 text-center text-sm">
                        No sections yet. Add one from the Layers panel to start building the page.
                    </p>
                </main>
            ) : null}
        </div>
    );
};
